import { useMemo } from 'react';

import { QueryError } from 'src/core/common/components/QueryError';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { type Budget } from 'src/core/modules/budgets/models/budget';
import { type PeriodRange } from 'src/core/modules/budgets/models/periodRange';

import { BudgetCardRow } from './BudgetCardRow';
import { useBreakdownQuery } from '../../../../containers/hooks/useBreakdownQuery';
import { type BudgetaryExercise } from '../../../../models/budgetaryExercise';
import { toPeriodRangePayload } from '../../../../models/periodRange';

export type BudgetCardProps = {
  budget: Budget;
  periodRange: PeriodRange;
  budgetaryExercise: BudgetaryExercise;
};

export const BudgetCard = ({
  budget,
  periodRange,
  budgetaryExercise,
}: BudgetCardProps) => {
  const { t } = useTranslation('global');
  const companyId = useCompanyId();

  const periodRangePayload = useMemo(
    () =>
      toPeriodRangePayload(
        budgetaryExercise.periodicity === 'yearly'
          ? { key: 'overall' }
          : periodRange,
      ),
    [budgetaryExercise.periodicity, periodRange],
  );

  const breakdownQueryState = useBreakdownQuery(budget.id, periodRangePayload);

  if (breakdownQueryState.status === 'error') {
    return (
      <QueryError
        queryError={breakdownQueryState.error}
        componentType="Callout"
      />
    );
  }

  if (breakdownQueryState.status === 'loading') {
    // as we support loading state in the container, we don't need to show a loader here
    return null;
  }

  return (
    <div className="box p-0 pb-8">
      <div className="mb-16 grid w-full grid-cols-4 px-16 pt-16 pl-[54px]">
        <div className="text-content-secondary-bg-primary body-m -ml-40">
          {t('budget.redesigned.budgetName')}
        </div>
        <div className="flex justify-between">
          <div className="text-content-secondary-bg-primary body-m">
            {t('budget.redesigned.utilization')}
          </div>
          <div className="text-content-secondary-bg-primary body-m">
            {t('budget.redesigned.budget')}
          </div>
        </div>
        <div className="text-content-secondary-bg-primary body-m text-right">
          {t('budget.redesigned.committed')}
        </div>
        <div className="text-content-secondary-bg-primary body-m text-right">
          {t('budget.redesigned.availableOrExceeded')}
        </div>
      </div>
      <BudgetCardRow
        className="mx-8 mb-0 ml-0 w-[calc(100%-16px)] px-8 py-8"
        budget={budget}
        periodRange={periodRange}
        breakdown={breakdownQueryState.data}
        companyId={companyId}
      />
    </div>
  );
};
